import type { GeometryElement as JXGElement } from 'jsxgraph';
import type { BoundLabel } from '@examdojo/core/widget';
import type { PortMap, PointPort, EdgePort, PortRegistry } from '../../../port';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import { FEATURE_LINE_DEFAULTS } from '../feature.defaults';
import type { MidsegmentElement } from './midsegment.model';

/**
 * Create a midsegment line connecting the midpoints of two edges of a shape.
 *
 * Reads the two edge ports from the parent shape's port registry,
 * computes the midpoint of each edge, and draws a dashed line between them.
 */
export function createMidsegment(
  element: MidsegmentElement & { id: string },
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const { elementFactory, portRegistry, displayConfig } = context;
  const jsxObjects: JXGElement[] = [];
  const labels: BoundLabel[] = [];

  const parentId = element.params.of;
  const [rawEdge1, rawEdge2] = element.params.edges;

  // Per-element display overrides
  const elementDisplay = displayConfig.elements?.[element.id];

  // Resolve edge names, trying the reversed form if the original doesn't exist
  // (e.g. 'AC' → 'CA', 'BA' → 'AB')
  const edgeName1 = resolveEdgeName(rawEdge1, parentId, portRegistry);
  const edgeName2 = resolveEdgeName(rawEdge2, parentId, portRegistry);

  // Resolve edge getters from the parent shape
  const getEdge1 = portRegistry.getGetter(`${parentId}.edge.${edgeName1}`);
  const getEdge2 = portRegistry.getGetter(`${parentId}.edge.${edgeName2}`);

  // Compute midpoints and draw the line at creation time
  const edge1 = getEdge1() as EdgePort;
  const edge2 = getEdge2() as EdgePort;
  const mid1 = midpoint(edge1.start, edge1.end);
  const mid2 = midpoint(edge2.start, edge2.end);

  const lineStyle = {
    color: elementDisplay?.line?.color ?? FEATURE_LINE_DEFAULTS.color,
    strokeWidth: elementDisplay?.line?.strokeWidth ?? FEATURE_LINE_DEFAULTS.strokeWidth,
    dash: elementDisplay?.line?.dash ?? FEATURE_LINE_DEFAULTS.dash,
    visible: elementDisplay?.visible ?? true,
    label: elementDisplay?.line?.label ?? (false as const),
    ...(elementDisplay?.line ?? {}),
    vertex: { fixed: true, ...(elementDisplay?.point ?? {}) },
  };

  const line = elementFactory.line({
    a: mid1,
    b: mid2,
    name: `${element.id}-midseg`,
    style: lineStyle,
  });

  jsxObjects.push(line.line, line.a, line.b);

  // --- Build ports ---
  const ports: PortMap = {
    length: () => {
      const e1 = getEdge1() as EdgePort;
      const e2 = getEdge2() as EdgePort;
      const m1 = midpoint(e1.start, e1.end);
      const m2 = midpoint(e2.start, e2.end);
      return dist(m1, m2);
    },
    midpoint1: () => {
      const e1 = getEdge1() as EdgePort;
      return midpoint(e1.start, e1.end);
    },
    midpoint2: () => {
      const e2 = getEdge2() as EdgePort;
      return midpoint(e2.start, e2.end);
    },
  };

  return {
    jsxObjects,
    labels,
    ports,
    destroy: () => {
      const board = elementFactory.getContext().board;
      for (const label of labels) {
        board.removeObject(label);
      }
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Math helpers
// ---------------------------------------------------------------------------

function midpoint(a: PointPort, b: PointPort): PointPort {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

function dist(a: PointPort, b: PointPort): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Resolve an edge name, trying the reversed form if the original doesn't exist.
 * e.g. 'AC' → try 'AC' first, fall back to 'CA'.
 */
function resolveEdgeName(name: string, parentId: string, registry: PortRegistry): string {
  if (registry.hasPort(`${parentId}.edge.${name}`)) {
    return name;
  }
  const reversed = name.split('').reverse().join('');
  if (registry.hasPort(`${parentId}.edge.${reversed}`)) {
    return reversed;
  }
  return name;
}
